import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

// A full-screen image lightbox — click-outside / Escape to close, body-scroll
// locked while open, and an onError fallback so an unhonoured size hint degrades
// to the original url rather than a broken image. Modeled on the community inbox's
// MessageAttachments lightbox (Part E1) and promoted to a shared primitive so the
// Content tab's image-forward post cards reuse the exact behaviour.
export function ImageLightbox({
  url,
  onClose,
  alt = 'Preview',
  /** Optional best-effort higher-res variant (e.g. Google photo size upgrade). */
  resolveSrc,
}: {
  url: string;
  onClose: () => void;
  alt?: string;
  resolveSrc?: (url: string) => string;
}) {
  const [src, setSrc] = useState(() => resolveSrc?.(url) ?? url);
  useEffect(() => {
    setSrc(resolveSrc?.(url) ?? url);
  }, [url, resolveSrc]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-6"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close preview"
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
      >
        <X className="h-5 w-5" />
      </button>
      <img
        src={src}
        alt={alt}
        onError={() => {
          if (src !== url) setSrc(url);
        }}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] max-w-full rounded-lg object-contain shadow-2xl"
      />
    </div>
  );
}
